/**
 * Task detail drawer — shared by the Tasks page and the Calendar page.
 * Shows the task, quick status actions, notes, reply composer, calendar
 * invite, and an inline edit form. `Select` is the small dropdown the Tasks
 * filters use too.
 */
import { useEffect, useState } from "react";
import { CATEGORIES, PRIORITIES, STATUSES, categoryColor, categoryLabel, chatTypeClasses, formatDateTime, priorityClasses, stripMentionIds } from "../lib/constants";
import { CheckCircle2, Trash2, X, Pencil, CalendarPlus, CalendarCheck, CalendarClock, AlertTriangle } from "lucide-react";
import { inviteIsCurrent } from "../lib/calendarPrefs";
import { toLocalInputValue } from "../lib/utils";
import ReplyComposer from "./ReplyComposer";
import DeadlinePicker from "./DeadlinePicker";
import { api } from "../lib/api";

export function StatusPill({ status }) {
  const map = {
    pending: "bg-slate-100 text-slate-700 border-slate-200",
    in_progress: "bg-blue-50 text-blue-700 border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-slate-100 text-slate-400 border-slate-200 line-through",
  };
  return (
    <span className={"text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded-sm " + (map[status] || map.pending)}>
      {STATUSES.find((s) => s.key === status)?.label || status}
    </span>
  );
}

export function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 px-2 border border-slate-200 rounded-sm bg-white text-[12.5px] focus:border-slate-900 outline-none"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function TaskDrawer({ task, onClose, onUpdate, onDelete, assignees = [], onSendInvite, sendingInvite = false }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({
    title: task.title,
    category: task.category,
    priority: task.priority,
    status: task.status,
    assignee: task.assignee || "",
    notes: task.notes || "",
    deadline: toLocalInputValue(task.deadline),
  });
  const [showCompleteSection, setShowCompleteSection] = useState(false);
  const [completionNote, setCompletionNote] = useState("");
  const [showNoteSection, setShowNoteSection] = useState(false);
  const [newNote, setNewNote] = useState("");

  const save = () => {
    const patch = {
      title: draft.title,
      category: draft.category,
      priority: draft.priority,
      status: draft.status,
      assignee: draft.assignee || null,
      notes: draft.notes,
      deadline: draft.deadline ? new Date(draft.deadline).toISOString() : null,
    };
    onUpdate(patch);
    setEdit(false);
  };

  const handleComplete = () => {
    const existingNotes = task.notes || "";
    const stamp = new Date().toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
    const merged = completionNote.trim()
      ? (existingNotes ? existingNotes + "\n\n" : "") + `[Completed · ${stamp}]\n${completionNote.trim()}`
      : existingNotes;
    onUpdate({ status: "completed", notes: merged });
    setShowCompleteSection(false);
    setCompletionNote("");
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const existingNotes = task.notes || "";
    const stamp = new Date().toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
    const merged = (existingNotes ? existingNotes + "\n\n" : "") + `[Note · ${stamp}]\n${newNote.trim()}`;
    onUpdate({ notes: merged });
    setShowNoteSection(false);
    setNewNote("");
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="task-drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto sw-fade-up">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="label-eyebrow">Task detail</div>
          <div className="flex gap-2">
            {!edit ? (
              <button onClick={() => setEdit(true)} className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] border border-slate-200 hover:border-slate-900 rounded-sm" data-testid="task-edit-btn">
                <Pencil size={13} /> Edit
              </button>
            ) : (
              <button onClick={save} className="h-8 px-3 text-[12.5px] bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition" data-testid="task-save-btn">Save</button>
            )}
            <button onClick={onDelete} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-600 border border-slate-200 rounded-sm" data-testid="task-delete-btn">
              <Trash2 size={14} />
            </button>
            <button onClick={onClose} className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-sm" data-testid="task-close-btn">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {!edit ? (
            <>
              <div>
                <div className="label-eyebrow">Title</div>
                <h2 className="font-heading text-2xl font-semibold mt-1">{task.title}</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded-sm"
                  style={{ borderColor: categoryColor(task.category), color: categoryColor(task.category) }}>
                  {categoryLabel(task.category)}
                </span>
                <span className={"text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded-sm " + priorityClasses(task.priority)}>
                  {task.priority}
                </span>
                <StatusPill status={task.status} />
                <span className={"text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded-sm " + chatTypeClasses(task.chat_type || "individual")}>
                  {task.chat_type || "individual"}
                </span>
              </div>

              {task.status !== "completed" && task.status !== "rejected" && (
                <div className="pt-2 border-t border-slate-200 space-y-2">
                  {/* Quick status row */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); onUpdate({ status: "in_progress" }); }}
                      className="flex-1 h-8 px-3 inline-flex items-center justify-center gap-1.5 text-[12.5px] bg-blue-50 text-blue-700 border border-blue-200 hover:border-blue-300 rounded-lg transition"
                      data-testid="quick-in-progress"
                    >
                      In Progress
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onUpdate({ status: "rejected" }); }}
                      className="flex-1 h-8 px-3 inline-flex items-center justify-center gap-1.5 text-[12.5px] bg-red-50 text-red-700 border border-red-200 hover:border-red-300 rounded-lg transition"
                      data-testid="quick-reject"
                    >
                      Reject
                    </button>
                  </div>

                  {/* Inline Mark Complete */}
                  {!showCompleteSection ? (
                    <button
                      onClick={() => setShowCompleteSection(true)}
                      className="w-full h-9 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2 transition"
                      data-testid="quick-complete-open"
                    >
                      <CheckCircle2 size={15} />
                      Mark as Complete
                    </button>
                  ) : (
                    <div className="space-y-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Completion Note (optional)</div>
                      <textarea
                        value={completionNote}
                        onChange={(e) => setCompletionNote(e.target.value)}
                        placeholder="What was done? Any follow-up needed?"
                        rows={3}
                        className="w-full p-2.5 border border-emerald-200 rounded-lg text-[13px] bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none transition"
                        autoFocus
                        data-testid="completion-note-input"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleComplete}
                          className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95"
                          data-testid="completion-confirm-inline"
                        >
                          Confirm Complete
                        </button>
                        <button
                          onClick={() => { setShowCompleteSection(false); setCompletionNote(""); }}
                          className="h-9 px-3 border border-emerald-200 hover:border-slate-400 rounded-xl text-[13px] text-slate-600 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {task.summary && (
                <div>
                  <div className="label-eyebrow">AI Summary</div>
                  <p className="text-[14px] text-slate-700 mt-1.5 italic">"{task.summary}"</p>
                </div>
              )}
              <Field label="Chat" value={task.chat_name} />
              <Field label="Sender" value={task.sender_name} />
              {task.sender_phone && (
                <Field label="Phone" value={task.sender_phone} />
              )}
              <Field label="Client" value={task.client_name || "—"} />
              <div>
                <Field label="Deadline" value={formatDateTime(task.deadline)} mono />
                {(task.status === "pending" || task.status === "in_progress") && (
                  <QuickReschedule task={task} onUpdate={onUpdate} />
                )}
                {task.deadline && (task.status === "pending" || task.status === "in_progress") && onSendInvite && (
                  <button
                    onClick={onSendInvite}
                    disabled={sendingInvite}
                    className={
                      "mt-1.5 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11.5px] font-semibold transition disabled:opacity-50 " +
                      (inviteIsCurrent(task)
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100")
                    }
                    data-testid="drawer-calendar-invite"
                  >
                    {inviteIsCurrent(task)
                      ? <><CalendarCheck size={12} /> On your calendar · resend</>
                      : <><CalendarPlus size={12} /> {sendingInvite ? "Sending…" : "Add to calendar"}</>}
                  </button>
                )}
                {task.calendar_invite?.last_error && (
                  <div className="text-[11px] text-red-600 mt-1">Last invite failed: {task.calendar_invite.last_error}</div>
                )}
                {(task.calendar_invite?.responses || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="invite-responses">
                    {task.calendar_invite.responses.map((r) => (
                      <span key={r.address} title={`${r.address} · ${new Date(r.dtstamp || r.recorded_at).toLocaleString()}`}
                        className={"inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2 py-0.5 " +
                          (r.status === "accepted" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : r.status === "declined" ? "text-red-700 bg-red-50 border-red-200"
                            : "text-amber-700 bg-amber-50 border-amber-200")}>
                        {r.status === "accepted" ? "✓" : r.status === "declined" ? "✕" : "?"} {r.name || r.address.split("@")[0]} {r.status}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Field label="Assignee" value={task.assignee || "Unassigned"} />
              <div>
                <div className="flex items-baseline justify-between">
                  <div className="label-eyebrow">Original WhatsApp message</div>
                  {task.message_received_at && (
                    <span className="text-[11px] text-slate-400 font-mono">
                      Received {formatDateTime(task.message_received_at)}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 p-3 bg-slate-50 border border-slate-200 rounded-sm text-[13.5px] whitespace-pre-wrap">
                  {stripMentionIds(task.original_text)}
                </div>
              </div>

              {task.chat_type === "individual" ? (
                <ReplyComposer task={task} onSent={() => onUpdate({})} />
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                  <div className="text-[12px] font-medium text-amber-900 mb-1">Group Task</div>
                  <p className="text-[12px] text-amber-800">
                    Replies are not available for group tasks. This task was created from a group chat and messages cannot be sent directly to groups.
                  </p>
                </div>
              )}

              {task.notes && (
                <div>
                  <div className="label-eyebrow">Notes</div>
                  <p className="text-[14px] text-slate-700 mt-1.5 whitespace-pre-wrap">{task.notes}</p>
                </div>
              )}

              {/* Inline Add Note */}
              <div className="border-t border-slate-100 pt-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Add Note</div>
                {!showNoteSection ? (
                  <button
                    onClick={() => setShowNoteSection(true)}
                    className="w-full h-9 border border-dashed border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl text-[12.5px] font-medium transition"
                    data-testid="add-note-open"
                  >
                    + Add a note…
                  </button>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note, update, or comment…"
                      rows={3}
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none resize-none transition"
                      autoFocus
                      data-testid="add-note-input"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95 disabled:opacity-50"
                        data-testid="add-note-save"
                      >
                        Save Note
                      </button>
                      <button
                        onClick={() => { setShowNoteSection(false); setNewNote(""); }}
                        className="h-9 px-3 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] text-slate-600 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-[11px] text-slate-400 font-mono pt-2 border-t border-slate-200 space-y-0.5">
                <div>Task created: {formatDateTime(task.created_at)}</div>
                <div>AI confidence: {Math.round((task.confidence || 0) * 100)}%</div>
              </div>
            </>
          ) : (
            <>
              <Edit label="Title">
                <input
                  className="w-full h-10 px-3 border border-slate-200 rounded-sm text-[14px] focus:border-slate-900 outline-none"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  data-testid="edit-title"
                />
              </Edit>
              <div className="grid grid-cols-2 gap-3">
                <Edit label="Category">
                  <select
                    className="w-full h-10 px-2 border border-slate-200 rounded-sm text-[14px] bg-white focus:border-slate-900 outline-none"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    data-testid="edit-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </Edit>
                <Edit label="Priority">
                  <select
                    className="w-full h-10 px-2 border border-slate-200 rounded-sm text-[14px] bg-white focus:border-slate-900 outline-none"
                    value={draft.priority}
                    onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                    data-testid="edit-priority"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </Edit>
                <Edit label="Status">
                  <select
                    className="w-full h-10 px-2 border border-slate-200 rounded-sm text-[14px] bg-white focus:border-slate-900 outline-none"
                    value={draft.status}
                    onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                    data-testid="edit-status"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </Edit>
                <Edit label="Deadline">
                  <input
                    type="datetime-local"
                    className="w-full h-10 px-2 border border-slate-200 rounded-sm text-[14px] focus:border-slate-900 outline-none"
                    value={draft.deadline}
                    onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                    data-testid="edit-deadline"
                  />
                </Edit>
              </div>
              <Edit label="Assignee">
                <select
                  className="w-full h-10 px-2 border border-slate-200 rounded-sm bg-white text-[14px] focus:border-slate-900 outline-none"
                  value={draft.assignee}
                  onChange={(e) => setDraft({ ...draft, assignee: e.target.value })}
                  data-testid="edit-assignee"
                >
                  <option value="">— Unassigned —</option>
                  {assignees.map((a) => (
                    <option key={a.id} value={a.name}>{a.name}{a.role ? ` · ${a.role}` : ""}</option>
                  ))}
                  {/* Keep current value selectable even if not in master list */}
                  {draft.assignee && !assignees.find((a) => a.name === draft.assignee) && (
                    <option value={draft.assignee}>{draft.assignee}</option>
                  )}
                </select>
              </Edit>
              <Edit label="Notes">
                <textarea
                  rows={4}
                  className="w-full p-2 border border-slate-200 rounded-sm text-[14px] focus:border-slate-900 outline-none"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  data-testid="edit-notes"
                />
              </Edit>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div>
    <div className="label-eyebrow">{label}</div>
    <div className={"text-[14px] text-slate-800 mt-1 " + (mono ? "font-mono text-[13px]" : "")}>{value}</div>
  </div>
);

const Edit = ({ label, children }) => (
  <div>
    <div className="label-eyebrow mb-1.5">{label}</div>
    {children}
  </div>
);


// ── Quick reschedule (under the deadline) ────────────────────────────────────

function lateBy(deadline) {
  const ms = Date.now() - new Date(deadline).getTime();
  if (!deadline || isNaN(ms) || ms <= 0) return null;
  const mins = Math.round(ms / 60000);
  return mins < 60 ? `${mins} min` : mins < 48 * 60 ? `${Math.round(mins / 60)} h` : `${Math.round(mins / 1440)} days`;
}

function shortWhen(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** One-tap moves to the next free slot today / tomorrow / in 2 days / in a
 *  week (same suggestions as the picker), +1 hour, or the full picker. */
function QuickReschedule({ task, onUpdate }) {
  const [suggestions, setSuggestions] = useState(null);
  const [picking, setPicking] = useState(false);
  const late = lateBy(task.deadline);

  useEffect(() => {
    let alive = true;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    api.get("/calendar/availability", { params: { date: today, exclude_task: task.id } })
      .then((r) => { if (alive) setSuggestions(r.data.suggestions || []); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [task.id, task.deadline]);

  const move = (iso) => onUpdate({ deadline: iso });
  const plusHour = () => {
    const base = Math.max(Date.now(), new Date(task.deadline || Date.now()).getTime());
    move(new Date(base + 60 * 60000).toISOString());
  };

  const chip = "h-8 px-3 inline-flex items-center gap-1.5 border rounded-lg text-[12px] font-medium transition active:scale-[0.98]";
  return (
    <div className="mt-2.5 p-3 border border-slate-200 rounded-xl bg-slate-50/60" data-testid="quick-reschedule">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 inline-flex items-center gap-1.5">
          <CalendarClock size={12} /> Reschedule
        </div>
        {late && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600">
            <AlertTriangle size={11} /> Overdue by {late}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={plusHour} className={chip + " border-slate-200 bg-white hover:border-slate-400 text-slate-700"} data-testid="resched-plus-hour">
          +1 hour
        </button>
        {(suggestions || []).map((sg) => (
          <button key={sg.label} onClick={() => move(sg.iso)} title={shortWhen(sg.iso)}
            className={chip + " border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/50 text-slate-700"}
            data-testid={`resched-${sg.label.replace(/\s+/g, "-")}`}>
            {sg.label} <span className="text-slate-400">{sg.time}</span>
          </button>
        ))}
        {suggestions === null && <span className="h-8 px-2 inline-flex items-center text-[12px] text-slate-400">Finding free slots…</span>}
        <button onClick={() => setPicking(true)} className={chip + " border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold"} data-testid="resched-pick">
          <CalendarClock size={13} /> Pick date & time…
        </button>
      </div>
      {picking && (
        <DeadlinePicker
          taskId={task.id}
          currentDeadline={task.deadline}
          onCancel={() => setPicking(false)}
          onSelect={(iso, opts) => {
            setPicking(false);
            const patch = { deadline: iso };
            if (iso && opts?.sendInvite !== undefined) patch.send_calendar_invite = opts.sendInvite;
            onUpdate(patch);
          }}
        />
      )}
    </div>
  );
}
